import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import defaultAvatar from "../../assets/noImagePerson.png";
import GoogleButton from "./googleButton";
import PasswordInput from "./passwordInput";
import PlaceTrainerSelect from "./placeTrainerSelect";
import "./auth.css";
import Turnstile, { turnstileEnabled } from "./turnstile";
import { Dots } from "../Loader/loader";

const TODAY = new Date().toISOString().slice(0, 10);

const PASSWORD_RULE = "Առնվազն 8 նիշ՝ տառերով և թվերով։";

const initialForm = {
  firstname: "",
  surname: "",
  patronymic: "",
  date_of_birth: "",
  sex: "",
  address: "",
  place_id: "",
  trainer_id: "",
  email: "",
  password: "",
  password_confirmation: "",
  hp_website: "", // honeypot: hidden from people, bots fill it in
};

const Register = () => {
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(defaultAvatar);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [openedAt] = useState(() => Date.now()); // the server rejects a form that was "filled" in seconds
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);

  useEffect(() => {
    if (!photo) {
      setPreview(defaultAvatar);
      return undefined;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  if (isAuthenticated) return <Navigate to="/profile" replace />;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handlePlaceTrainer = (changes) => {
    setForm((prev) => ({ ...prev, ...changes }));
    setErrors((prev) => ({ ...prev, place_id: undefined, trainer_id: undefined }));
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file) setPhoto(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setErrors({});

    if (form.password !== form.password_confirmation) {
      setErrors({ password_confirmation: ["Գաղտնաբառերը չեն համընկնում։"] });
      return;
    }

    if (turnstileEnabled && !captchaToken) {
      setFormError("Հաստատեք, որ ռոբոտ չեք։");
      return;
    }

    const data = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (value !== "") data.append(key, value);
    });
    if (photo) data.append("photo", photo);
    data.append("form_started_at", String(openedAt));
    if (captchaToken) data.append("captcha_token", captchaToken);

    setSubmitting(true);
    try {
      await register(data);
      navigate("/profile");
    } catch (err) {
      setCaptchaReset((n) => n + 1);
      if (err.response?.status === 429) {
        setFormError("Չափից շատ փորձեր։ Խնդրում ենք փորձել մի փոքր ուշ։");
      } else if (err.response?.status === 422) {
        setErrors(err.response.data.errors || {});
        setFormError("Ստուգեք լրացված տվյալները։");
      } else {
        setFormError("Գրանցումը չհաջողվեց։ Փորձեք կրկին։");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = (name) => `auth-field${errors[name] ? " invalid" : ""}`;
  const fieldError = (name) =>
    errors[name] && <span className="auth-error">{errors[name][0]}</span>;

  return (
    <div className="auth-page">
      <div className="auth-card wide">
        <h2 className="auth-title">Գրանցում</h2>
        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {formError && <div className="auth-alert">{formError}</div>}

          <div className="avatar-upload">
            <img className="avatar-circle" src={preview} alt="avatar" />
            <label className="avatar-btn" htmlFor="photo">
              Ընտրել լուսանկար
            </label>
            <input id="photo" type="file" accept="image/*" onChange={handlePhoto} />
            {errors.photo && <span className="auth-error">{errors.photo[0]}</span>}
          </div>

          <div className="auth-row">
            <div className={fieldClass("firstname")}>
              <label htmlFor="firstname">Անուն</label>
              <input id="firstname" name="firstname" value={form.firstname} onChange={handleChange} />
              {fieldError("firstname")}
            </div>
            <div className={fieldClass("surname")}>
              <label htmlFor="surname">Ազգանուն</label>
              <input id="surname" name="surname" value={form.surname} onChange={handleChange} />
              {fieldError("surname")}
            </div>
          </div>

          <div className={fieldClass("patronymic")}>
            <label htmlFor="patronymic">Հայրանուն</label>
            <input id="patronymic" name="patronymic" value={form.patronymic} onChange={handleChange} />
            {fieldError("patronymic")}
          </div>

          <div className="auth-row">
            <div className={fieldClass("date_of_birth")}>
              <label htmlFor="date_of_birth">Ծննդյան ամսաթիվ</label>
              <input id="date_of_birth" name="date_of_birth" type="date" min="1930-01-01" max={TODAY} value={form.date_of_birth} onChange={handleChange} />
              {fieldError("date_of_birth")}
            </div>
            <div className={fieldClass("sex")}>
              <label htmlFor="sex">Սեռ</label>
              <select id="sex" name="sex" value={form.sex} onChange={handleChange}>
                <option value="">Ընտրեք սեռը</option>
                <option value="Արական">Արական</option>
                <option value="Իգական">Իգական</option>
              </select>
              {fieldError("sex")}
            </div>
          </div>

          <div className={fieldClass("address")}>
            <label htmlFor="address">Բնակության հասցե</label>
            <input id="address" name="address" value={form.address} onChange={handleChange} />
            {fieldError("address")}
          </div>

          <PlaceTrainerSelect
            placeId={form.place_id}
            trainerId={form.trainer_id}
            onChange={handlePlaceTrainer}
            errors={errors}
          />



          <div className={fieldClass("email")}>
            <label htmlFor="email">Էլ. հասցե</label>
            <input id="email" name="email" type="email" autoComplete="email" value={form.email} onChange={handleChange} />
            {fieldError("email")}
          </div>

          <div className="auth-row stack">
            <div className={fieldClass("password")}>
              <label htmlFor="password">Գաղտնաբառ</label>
              <PasswordInput id="password" name="password" autoComplete="new-password" value={form.password} onChange={handleChange} />
              {errors.password ? (
                <span className="auth-error">{PASSWORD_RULE}</span>
              ) : (
                <span className="auth-hint">{PASSWORD_RULE}</span>
              )}
            </div>
            <div className={fieldClass("password_confirmation")}>
              <label htmlFor="password_confirmation">Կրկնել գաղտնաբառը</label>
              <PasswordInput id="password_confirmation" name="password_confirmation" autoComplete="new-password" value={form.password_confirmation} onChange={handleChange} />
              {fieldError("password_confirmation")}
            </div>
          </div>

          {/* Honeypot: invisible to people (and to screen readers), scripts tend to fill it in */}
          <div className="hp-field" aria-hidden="true">
            <label htmlFor="hp_website">Leave this field empty</label>
            <input
              id="hp_website"
              name="hp_website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.hp_website}
              onChange={handleChange}
            />
          </div>

          <Turnstile onToken={setCaptchaToken} resetKey={captchaReset} />

          <button className="auth-btn" type="submit" disabled={submitting}>
            {submitting ? <Dots /> : "Գրանցվել"}
          </button>
        </form>
        <GoogleButton
          onSuccess={() => navigate("/profile")}
          onError={setFormError}
          onTwoFactor={() => setFormError("Այս հաշիվն ունի 2FA։ Մուտք գործեք «Մուտք» էջից։")}
        />
        <p className="auth-switch">
          Արդեն ունե՞ք հաշիվ։ <Link to="/login">Մուտք գործել</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
